import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  Bell, ArrowLeft, RefreshCw, Save, Mail, AlertTriangle, Clock, Shield,
  CheckCircle, XCircle, Loader2, Send, Activity, Calendar, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NOTIFICATION_TYPES = {
  'tmf_critical_alert': { label: 'Critical Alert', color: 'bg-red-500', icon: AlertTriangle },
  'tmf_deadline_reminder': { label: 'Deadline Reminder', color: 'bg-amber-500', icon: Clock },
  'tmf_low_completeness': { label: 'Low Completeness', color: 'bg-orange-500', icon: Activity },
  'tmf_daily_digest': { label: 'Daily Digest', color: 'bg-blue-500', icon: Calendar },
  'test': { label: 'Test', color: 'bg-green-500', icon: CheckCircle }
};

const STATUS_COLORS = {
  'sent': 'bg-green-500/20 text-green-600',
  'mocked': 'bg-blue-500/20 text-blue-600',
  'pending': 'bg-amber-500/20 text-amber-600',
  'failed': 'bg-red-500/20 text-red-600'
};

export default function NotificationSettingsPage() {
  const { getAuthHeaders, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [triggeringCheck, setTriggeringCheck] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  
  // Preferences state
  const [preferences, setPreferences] = useState({
    email_enabled: true,
    tmf_alerts: {
      critical_missing_docs: true,
      deadline_reminders: true,
      low_completeness: true,
      compliance_issues: true
    },
    alert_frequency: 'immediate',
    quiet_hours: {
      enabled: false,
      start: '22:00',
      end: '08:00'
    }
  });
  
  // History state
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { headers } = getAuthHeaders();

      // Fetch preferences
      const prefsRes = await axios.get(`${API_URL}/api/notifications/preferences`, { headers });
      setPreferences(prefsRes.data);

      // Fetch history
      const historyRes = await axios.get(`${API_URL}/api/notifications/history?limit=50`, { headers });
      setHistory(historyRes.data?.notifications || []);

      // Fetch stats
      const statsRes = await axios.get(`${API_URL}/api/notifications/stats`, { headers });
      setStats(statsRes.data);

    } catch (error) {
      console.error('Error fetching notification data:', error);
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const savePreferences = async () => {
    setSaving(true);
    try {
      const { headers } = getAuthHeaders();
      await axios.put(`${API_URL}/api/notifications/preferences`, preferences, { headers });
      toast.success('Notification preferences saved');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const sendTestNotification = async () => {
    setSendingTest(true);
    try {
      const { headers } = getAuthHeaders();
      const response = await axios.post(`${API_URL}/api/notifications/test`, {}, { headers });
      
      if (response.data.success) {
        toast.success(`Test notification ${response.data.status === 'mocked' ? 'logged (email mocked)' : 'sent'}`);
        await fetchData(); // Refresh history
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error('Failed to send test notification');
    } finally {
      setSendingTest(false);
    }
  };

  const triggerAlertCheck = async () => {
    setTriggeringCheck(true);
    try {
      const { headers } = getAuthHeaders();
      const response = await axios.post(`${API_URL}/api/notifications/trigger-alert-check`, {}, { headers });
      
      if (response.data.success) {
        const result = response.data.result;
        toast.success(`Alert check completed: ${result.total_alerts} alerts found, ${result.recipients_notified} users notified`);
        await fetchData();
      }
    } catch (error) {
      console.error('Error triggering alert check:', error);
      toast.error('Failed to trigger alert check');
    } finally {
      setTriggeringCheck(false);
    }
  };

  const updatePreference = (path, value) => {
    setPreferences(prev => {
      const newPrefs = { ...prev };
      const keys = path.split('.');
      let current = newPrefs;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newPrefs;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading notification settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-700/50 bg-gradient-to-r from-slate-900 to-slate-800 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard?view=all')}
                className="text-white/70 hover:text-white hover:bg-slate-50/10"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-50/20"></div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Notification Settings</h1>
                  <p className="text-xs text-white/60">Email Alerts & Preferences</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={sendTestNotification}
                disabled={sendingTest}
                className="border-white/20 text-white hover:bg-slate-50/10"
                data-testid="test-notification-btn"
              >
                {sendingTest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Test Notification
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                className="border-white/20 text-white hover:bg-slate-50/10"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100/80 border border-slate-300 mb-6">
            <TabsTrigger value="settings" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-settings">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-history">
              <Clock className="w-4 h-4 mr-2" />
              History ({history.length})
            </TabsTrigger>
            {isAdmin() && (
              <TabsTrigger value="admin" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-admin">
                <Shield className="w-4 h-4 mr-2" />
                Admin Actions
              </TabsTrigger>
            )}
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6" data-testid="settings-content">
            <div className="grid grid-cols-2 gap-6">
              {/* Email Notifications */}
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-blue-600" />
                    Email Notifications
                  </CardTitle>
                  <CardDescription>Control how you receive email alerts</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-slate-900 font-medium">Enable Email Notifications</Label>
                      <p className="text-sm text-slate-500">Receive alerts via email</p>
                    </div>
                    <Switch
                      checked={preferences.email_enabled}
                      onCheckedChange={(checked) => updatePreference('email_enabled', checked)}
                      data-testid="email-enabled-switch"
                    />
                  </div>

                  <Separator />

                  <div>
                    <Label className="text-slate-900 font-medium mb-3 block">Alert Frequency</Label>
                    <Select 
                      value={preferences.alert_frequency} 
                      onValueChange={(value) => updatePreference('alert_frequency', value)}
                    >
                      <SelectTrigger data-testid="frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immediate">Immediate</SelectItem>
                        <SelectItem value="daily_digest">Daily Digest</SelectItem>
                        <SelectItem value="weekly_digest">Weekly Digest</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-slate-900 font-medium">Quiet Hours</Label>
                        <p className="text-sm text-slate-500">Pause notifications during specific hours</p>
                      </div>
                      <Switch
                        checked={preferences.quiet_hours?.enabled || false}
                        onCheckedChange={(checked) => updatePreference('quiet_hours.enabled', checked)}
                      />
                    </div>
                    
                    {preferences.quiet_hours?.enabled && (
                      <div className="grid grid-cols-2 gap-4 pl-4">
                        <div>
                          <Label className="text-sm text-slate-600">Start Time</Label>
                          <Input
                            type="time"
                            value={preferences.quiet_hours?.start || '22:00'}
                            onChange={(e) => updatePreference('quiet_hours.start', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-slate-600">End Time</Label>
                          <Input
                            type="time"
                            value={preferences.quiet_hours?.end || '08:00'}
                            onChange={(e) => updatePreference('quiet_hours.end', e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* TMF Alert Types */}
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    TMF Alert Types
                  </CardTitle>
                  <CardDescription>Choose which alerts you want to receive</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                        <XCircle className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <Label className="text-slate-900 font-medium">Critical Missing Documents</Label>
                        <p className="text-xs text-slate-500">High-priority missing docs alerts</p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.tmf_alerts?.critical_missing_docs ?? true}
                      onCheckedChange={(checked) => updatePreference('tmf_alerts.critical_missing_docs', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <Clock className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <Label className="text-slate-900 font-medium">Deadline Reminders</Label>
                        <p className="text-xs text-slate-500">Upcoming document deadlines</p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.tmf_alerts?.deadline_reminders ?? true}
                      onCheckedChange={(checked) => updatePreference('tmf_alerts.deadline_reminders', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-orange-50 border border-orange-200">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                        <Activity className="w-4 h-4 text-orange-600" />
                      </div>
                      <div>
                        <Label className="text-slate-900 font-medium">Low Completeness</Label>
                        <p className="text-xs text-slate-500">When TMF completeness drops</p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.tmf_alerts?.low_completeness ?? true}
                      onCheckedChange={(checked) => updatePreference('tmf_alerts.low_completeness', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-purple-50 border border-purple-200">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <Shield className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <Label className="text-slate-900 font-medium">Compliance Issues</Label>
                        <p className="text-xs text-slate-500">Regulatory compliance alerts</p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.tmf_alerts?.compliance_issues ?? true}
                      onCheckedChange={(checked) => updatePreference('tmf_alerts.compliance_issues', checked)}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button onClick={savePreferences} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Preferences
              </Button>
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4" data-testid="history-content">
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-4 gap-4 mb-6">
                <Card className="bg-slate-50/90 border-slate-300">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-600">Last 24 Hours</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.last_24h || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-green-600">Sent</p>
                    <p className="text-2xl font-bold text-green-700">{stats.by_status?.sent || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-blue-600">Mocked</p>
                    <p className="text-2xl font-bold text-blue-700">{stats.by_status?.mocked || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-red-600">Failed</p>
                    <p className="text-2xl font-bold text-red-700">{stats.by_status?.failed || 0}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {history.length === 0 ? (
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-12 text-center">
                  <Bell className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Notification History</h3>
                  <p className="text-slate-600">Your notification history will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {history.map((notification, idx) => {
                    const typeInfo = NOTIFICATION_TYPES[notification.notification_type] || { label: notification.notification_type, color: 'bg-slate-500', icon: Bell };
                    const TypeIcon = typeInfo.icon;
                    
                    return (
                      <Card key={idx} className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-lg ${typeInfo.color}/20 flex items-center justify-center`}>
                                <TypeIcon className={`w-5 h-5 ${typeInfo.color.replace('bg-', 'text-').replace('/20', '')}`} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-slate-900">{notification.subject}</p>
                                  <Badge className={STATUS_COLORS[notification.status] || 'bg-slate-200'}>{notification.status}</Badge>
                                </div>
                                <p className="text-sm text-slate-500">
                                  {notification.created_at ? format(parseISO(notification.created_at), 'MMM dd, yyyy HH:mm') : 'N/A'}
                                </p>
                              </div>
                            </div>
                            <Badge className={`${typeInfo.color}/20 ${typeInfo.color.replace('bg-', 'text-')}`}>
                              {typeInfo.label}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </TabsContent>

          {/* Admin Tab */}
          {isAdmin() && (
            <TabsContent value="admin" className="space-y-4" data-testid="admin-content">
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900">Admin Actions</CardTitle>
                  <CardDescription>Manually trigger notification checks</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                    <div>
                      <h4 className="font-medium text-slate-900">Trigger TMF Alert Check</h4>
                      <p className="text-sm text-slate-500">Scan all studies for critical alerts and notify relevant users</p>
                    </div>
                    <Button 
                      onClick={triggerAlertCheck} 
                      disabled={triggeringCheck}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {triggeringCheck ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                      Run Alert Check
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                    <div>
                      <h4 className="font-medium text-slate-900">Send Daily Digest</h4>
                      <p className="text-sm text-slate-500">Send TMF health summary to all admin users</p>
                    </div>
                    <Button 
                      onClick={async () => {
                        try {
                          const { headers } = getAuthHeaders();
                          const res = await axios.post(`${API_URL}/api/notifications/send-daily-digest`, {}, { headers });
                          toast.success(`Daily digest sent to ${res.data.recipients_count} users`);
                        } catch (e) {
                          toast.error('Failed to send daily digest');
                        }
                      }}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Send Digest
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Email Mode: {process.env.REACT_APP_EMAIL_MOCKED === 'true' ? 'Mocked' : 'Live'}</p>
                      <p className="text-sm text-amber-700">
                        When mocked, emails are logged but not actually sent. This is useful for development and testing.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
